import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Stepper,
  Step,
  StepLabel,
  Card,
  CardContent,
  Button,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControlLabel,
  Checkbox,
  RadioGroup,
  Radio,
  SelectChangeEvent,
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { formatToUIDate } from '../utils/dateUtils';

// Mock data for residents
const mockResidents = [
  { id: 1, name: 'John Smith', room: '201', admissionDate: '2023-01-15' },
  { id: 2, name: 'Mary Johnson', room: '202', admissionDate: '2023-02-10' },
  { id: 3, name: 'Robert Williams', room: '203', admissionDate: '2023-03-05' },
];

const mdsSections = [
  { id: 'section_a', title: 'Identification Information', description: 'Resident demographic and administrative information' },
  { id: 'section_b', title: 'Coverage for Medicare/Medicaid', description: 'Insurance and coverage information' },
  { id: 'section_c', title: 'Resident Characteristics', description: 'Physical and mental characteristics' },
  { id: 'section_d', title: 'Active Diagnoses', description: 'Current diagnoses and conditions' },
  { id: 'section_e', title: 'Functional Capability', description: 'Resident functional abilities' },
  { id: 'section_f', title: 'Mood and Behavior Patterns', description: 'Psychological status assessment' },
  { id: 'section_g', title: 'Medical Conditions', description: 'Specific medical conditions and treatments' },
  { id: 'section_h', title: 'Treatments and Procedures', description: 'Medical treatments and procedures' },
  { id: 'section_i', title: 'Oral and Dental Health', description: 'Dental and oral health status' },
  { id: 'section_j', title: 'Vision', description: 'Vision assessment' },
  { id: 'section_k', title: 'Hearing, Speech and Communication', description: 'Communication abilities' },
  { id: 'section_l', title: 'Emergency and Urgent Care', description: 'Emergency care history' },
  { id: 'section_m', title: 'Special Treatments, Procedures, and Programs', description: 'Special treatments and programs' },
  { id: 'section_n', title: 'Therapy', description: 'Therapy services' },
  { id: 'section_o', title: 'Discharge Planning', description: 'Discharge planning information' },
  { id: 'section_p', title: 'Quality Measures', description: 'Quality measures and outcomes' },
  { id: 'section_q', title: 'Rehospitalization Assessment', description: 'Risk of rehospitalization' },
  { id: 'section_r', title: 'Selected Infections and Related Treatments', description: 'Infection monitoring' },
  { id: 'section_s', title: 'Oral and Dental Health', description: 'Dental care services' },
  { id: 'section_t', title: 'Behavioral Symptoms and Cognitive Patterns', description: 'Behavioral and cognitive assessment' },
  { id: 'section_u', title: 'Specialized Assessments', description: 'Specialized clinical assessments' },
  { id: 'section_v', title: 'Physical Therapy', description: 'Physical therapy assessment' },
  { id: 'section_w', title: 'Occupational Therapy', description: 'Occupational therapy assessment' },
  { id: 'section_x', title: 'Speech-Language Pathology', description: 'Speech therapy assessment' },
  { id: 'section_y', title: 'MDS Section Y', description: 'Additional assessment items' },
  { id: 'section_z', title: 'MDS Section Z', description: 'Final assessment items' },
];

const MdsAssessmentPage: React.FC = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [selectedResident, setSelectedResident] = useState<number | null>(null);
  const [assessmentType, setAssessmentType] = useState('');
  const [referenceDate, setReferenceDate] = useState<Date | null>(null);
  const [assessmentData, setAssessmentData] = useState<Record<string, any>>({});

  const steps = [
    'Select Resident',
    'Assessment Details',
    'MDS Sections',
    'Review & Submit'
  ];

  const handleResidentChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedResident(Number(event.target.value));
  };

  const handleAssessmentTypeChange = (event: SelectChangeEvent<string>) => {
    setAssessmentType(event.target.value);
  };

  const handleDataChange = (sectionId: string, field: string, value: any) => {
    setAssessmentData(prev => ({
      ...prev,
      [sectionId]: {
        ...prev[sectionId],
        [field]: value
      }
    }));
  };

  const getStepContent = (step: number) => {
    switch (step) {
      case 0: // Select Resident
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Select Resident for MDS Assessment
            </Typography>
            <RadioGroup value={selectedResident} onChange={handleResidentChange}>
              {mockResidents.map((resident) => (
                <FormControlLabel
                  key={resident.id}
                  value={resident.id}
                  control={<Radio />}
                  label={`${resident.name} (Room: ${resident.room}, Admitted: ${resident.admissionDate})`}
                />
              ))}
            </RadioGroup>
          </Box>
        );

      case 1: // Assessment Details
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Assessment Details
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Assessment Type</InputLabel>
                  <Select
                    value={assessmentType}
                    label="Assessment Type"
                    onChange={handleAssessmentTypeChange}
                  >
                    <MenuItem value="admission">Admission</MenuItem>
                    <MenuItem value="5day">5 Day</MenuItem>
                    <MenuItem value="14day">14 Day</MenuItem>
                    <MenuItem value="annual">Annual</MenuItem>
                    <MenuItem value="significant_change">Significant Change</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label="Reference Date"
                    value={referenceDate}
                    onChange={(newValue) => setReferenceDate(newValue)}
                    slotProps={{
                      textField: {}
                    }}
                  />
                </LocalizationProvider>
              </Grid>
            </Grid>
          </Box>
        );

      case 2: // MDS Sections
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              MDS Assessment Sections
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Complete the required sections for the MDS 3.0 assessment. Sections are organized by category.
            </Typography>

            {mdsSections.map((section) => (
              <Accordion key={section.id} defaultExpanded={section.id === 'section_a'}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography>{section.title}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {section.description}
                  </Typography>
                  <TextField
                    fullWidth
                    label="Assessment Notes"
                    multiline
                    rows={3}
                    value={assessmentData[section.id]?.notes || ''}
                    onChange={(e) => handleDataChange(section.id, 'notes', e.target.value)}
                    placeholder="Enter assessment details here..."
                  />
                  <Box sx={{ mt: 1 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={assessmentData[section.id]?.completed || false}
                          onChange={(e) => handleDataChange(section.id, 'completed', e.target.checked)}
                        />
                      }
                      label="Section Completed"
                    />
                  </Box>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        );

      case 3: // Review & Submit
        const selectedResidentInfo = mockResidents.find(r => r.id === selectedResident);
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Review MDS Assessment
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Please review the assessment details before submission.
            </Typography>

            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2">Resident:</Typography>
                    <Typography>{selectedResidentInfo?.name}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2">Assessment Type:</Typography>
                    <Typography>{assessmentType}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2">Reference Date:</Typography>
                    <Typography>{formatToUIDate(referenceDate)}</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2">Sections Completed:</Typography>
                    <Typography>
                      {Object.keys(assessmentData).filter(
                        sectionId => assessmentData[sectionId].completed
                      ).length} of {mdsSections.length} sections
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            <Typography variant="body2" color="text.secondary">
              MDS assessment will be submitted for validation and used for PDPM reimbursement calculations.
            </Typography>
          </Box>
        );

      default:
        return <div>Unknown step</div>;
    }
  };

  const handleNext = () => {
    setActiveStep(activeStep + 1);
  };

  const handleBack = () => {
    setActiveStep(activeStep - 1);
  };

  const handleSubmit = () => {
    // In a real app, this would submit to an API
    alert('MDS assessment submitted successfully!');
  };

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        MDS Assessment
      </Typography>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Stepper activeStep={activeStep} alternativeLabel>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Paper>

      <Paper sx={{ p: 3 }}>
        {getStepContent(activeStep)}

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
          {activeStep > 0 && (
            <Button onClick={handleBack}>Back</Button>
          )}
          {activeStep < steps.length - 1 ? (
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={activeStep === 0 && selectedResident === null}
              sx={{ ml: 'auto' }}
            >
              Next
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={handleSubmit}
              sx={{ ml: 'auto' }}
            >
              Submit Assessment
            </Button>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default MdsAssessmentPage;